import React, { useEffect, useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import api from '../../../../api/api'
import FeaturedTable from '../../../FeaturedTable/FeaturedTable'
import ConfirmPopup from '../../../Popups/ConfirmPopup'
import RadioField from '../../../FormLegacy/FormFieldsV2/RadioField'
import Icon from '../../../Icons'
import { conflictReservationsColumns } from '../../helpers'
import { useSnackbar } from '../../../../helpers/hooks/useSnackbar'

const ConflictReservations = ({
  title,
  formState,
  buildingData,
  reservedTimeRanges,
  onBack = () => {},
  onNext = () => {},
}) => {
  const { t } = useTranslation(['reservations', 'common'])
  const [isLoading, setIsLoading] = useState(false)
  const [refundState, setRefundState] = useState('refund')
  const [cancelReservations, setCancelReservations] = useState(false)
  const [reservations, setReservations] = useState([])
  const { timezone, id: buildingId } = buildingData
  const { displaySnackbar } = useSnackbar()

  const fetchAmenityReservations = async () => {
    setIsLoading(true)
    try {
      const data = await api.getReservationsInDateRange(
        buildingId,
        formState.amenity.id,
        formState.date,
        formState.endDate ? formState.endDate : new Date(formState.date).setHours(23, 59, 0, 0),
      )
      const reservationsData = data.filter((reservation) => {
        if (reservedTimeRanges.length > 0) {
          return reservedTimeRanges.includes(reservation.startTime)
        }
        return true
      })

      setReservations(reservationsData)
    } catch ({ message }) {
      console.error(message)
      displaySnackbar(message, true)
    }
    setIsLoading(false)
  }

  const onCancelReservations = async () => {
    setIsLoading(true)
    try {
      const reservationIds = reservations.map((reservation) => reservation.id)
      await api.cancelAmenityReservations(
        { reservations: reservationIds, refund: refundState === 'refund' },
        formState.amenity.id,
      )
      await fetchAmenityReservations()
      setCancelReservations(false)
    } catch ({ message }) {
      console.error(message)
      displaySnackbar(message, true)
    }
    setIsLoading(false)
  }

  const getCancelReservationBody = useCallback(
    () => (
      <div className="cancel-reservation-confirm-body">
        <span>{t('reservations:conflictReservations.confirmCancelAllBody')}</span>
        <RadioField
          type="radio"
          name="cancelOption"
          label={t('reservations:conflictReservations.cancelWithRefund')}
          onChange={(e) => setRefundState(e.target.value)}
          checked={refundState === 'refund'}
          value="refund"
        />
        <RadioField
          type="radio"
          name="cancelOption"
          label={t('reservations:conflictReservations.cancelWithoutRefund')}
          onChange={(e) => setRefundState(e.target.value)}
          checked={refundState === 'no_refund'}
          value="no_refund"
        />
      </div>
    ),
    [refundState],
  )

  useEffect(() => {
    fetchAmenityReservations()
  }, [])

  return (
    <React.Fragment>
      <div className="reservations-modal-content reservations-add-time-block-modal">
        <div className="reservations-modal-actions left" style={{ padding: 0 }}>
          <button className="button button-gray" onClick={onBack}>
            {t('common:actions.back')}
          </button>
        </div>
        <div className={`conflicting-reservations-container ${isLoading ? 'is-loading' : ''}`}>
          <span className="title">{title}</span>
          {isLoading ? null : reservations.length > 0 ? (
            <React.Fragment>
              <div className="reservations-list">
                <div className="reservations-list-table" style={{ maxHeight: 300, padding: 0 }}>
                  <FeaturedTable
                    columns={conflictReservationsColumns(
                      {
                        timezone,
                        multiDayBooking: formState.amenity.multiDayBooking,
                      },
                      t,
                    )}
                    data={reservations}
                  />
                </div>
              </div>
              <div
                className="reservations-modal-actions left"
                style={{ padding: 0, marginBottom: 10 }}
              >
                <button
                  className="button button-red inverted-colors"
                  onClick={() => setCancelReservations(true)}
                >
                  {t('reservations:conflictReservations.cancelAllReservations')}
                </button>
              </div>
            </React.Fragment>
          ) : (
            <div className="no-conflicting-reservations">
              <Icon icon="circleCheck" />
              <span>{t('reservations:conflictReservations.noConflictFoundTitle')}</span>
              <span>{t('reservations:conflictReservations.noConflictFoundDescription')}</span>
              <button className="button" onClick={onNext}>
                {t('reservations:conflictReservations.continueCreatingTimeBlock')}
              </button>
            </div>
          )}
        </div>
      </div>
      {cancelReservations && (
        <ConfirmPopup
          title={t('reservations:conflictReservations.confirmationTitle')}
          cancelText={t('common:actions.cancel')}
          confirmText={t('reservations:conflictReservations.confirmCancelReservations')}
          isOpen={cancelReservations}
          body={getCancelReservationBody()}
          cancelCallback={() => setCancelReservations(false)}
          confirmCallback={onCancelReservations}
        />
      )}
    </React.Fragment>
  )
}

ConflictReservations.propTypes = {
  onBack: PropTypes.func,
  onNext: PropTypes.func,
  title: PropTypes.string,
  formState: PropTypes.object,
  buildingData: PropTypes.object,
  reservedTimeRanges: PropTypes.array,
}

export default ConflictReservations
